import { SKY_DRAGON_NOSTRILS } from '../../../packages/domain/src/84-sky-prototype-rig.mjs';
import { SKY_WORLD_ASSETS } from '../../../packages/domain/src/86-sky-world-scale.mjs';

/** Add a local, owned cartilage morph to the actual skinned snout. Anchors are
 * measured in Head_2 space; a weighted inverse skin transform converts the
 * small world-space flare back into each affected vertex's original bind space.
 * Shared imported geometry, textures, joints and animation clips remain intact.
 * Generic rigs without the measured Head_2 anchor retain their original skin. */
export function prepareSkyNostrils(T, model, head) {
  if (!head?.isObject3D) return { meshes: [], update() {}, dispose() {} };
  const meshes = [],
    geometries = new Set();
  let disposed = false;
  model.updateMatrixWorld(true);
  const scale = head.getWorldScale(new T.Vector3()).x / 16;
  const anchors = SKY_DRAGON_NOSTRILS.map(({ position, direction }) => ({
    point: head.localToWorld(new T.Vector3(...position)),
    normal: new T.Vector3(...direction).transformDirection(head.matrixWorld),
  }));
  const world = new T.Vector3(),
    offset = new T.Vector3(),
    delta = new T.Vector3(),
    component = new T.Vector3();
  const skin = new T.Matrix4(),
    joint = new T.Matrix4(),
    transform = new T.Matrix4(),
    inverse = new T.Matrix3();
  model.traverse((mesh) => {
    if (!mesh.isSkinnedMesh) return;
    const source = mesh.geometry,
      position = source.attributes.position;
    const values = new Float32Array(position.count * 3);
    let affected = 0;
    for (let index = 0; index < position.count; index++) {
      mesh.getVertexPosition(index, world).applyMatrix4(mesh.matrixWorld);
      delta.set(0, 0, 0);
      for (const anchor of anchors) {
        offset.copy(world).sub(anchor.point);
        const distance = offset.length(),
          radius = 0.09 * scale;
        if (distance >= radius) continue;
        const weight = (1 - distance / radius) ** 2;
        component.copy(offset).addScaledVector(anchor.normal, -offset.dot(anchor.normal));
        delta
          .addScaledVector(component, 0.32 * weight)
          .addScaledVector(anchor.normal, 0.014 * scale * weight);
      }
      if (delta.lengthSq() < 1e-14) continue;
      // Skinning is linear in the bind-space position. Invert exactly the same
      // weighted matrices as the renderer, rather than assuming a rigid snout.
      skin.elements.fill(0);
      for (let slot = 0; slot < 4; slot++) {
        const weight = source.attributes.skinWeight.getComponent(index, slot);
        if (!weight) continue;
        const bone = source.attributes.skinIndex.getComponent(index, slot);
        joint.multiplyMatrices(
          mesh.skeleton.bones[bone].matrixWorld,
          mesh.skeleton.boneInverses[bone],
        );
        for (let value = 0; value < 16; value++)
          skin.elements[value] += joint.elements[value] * weight;
      }
      transform
        .copy(mesh.matrixWorld)
        .multiply(mesh.bindMatrixInverse)
        .multiply(skin)
        .multiply(mesh.bindMatrix)
        .invert();
      inverse.setFromMatrix4(transform);
      delta.applyMatrix3(inverse);
      values.set(delta.toArray(), index * 3);
      affected++;
    }
    if (!affected) return;
    const geometry = source.clone(),
      morph = new T.Float32BufferAttribute(values, 3);
    morph.name = 'NostrilFlare';
    geometry.morphTargetsRelative = true;
    geometry.morphAttributes.position = [morph];
    mesh.geometry = geometry;
    mesh.updateMorphTargets();
    mesh.userData.nostrilVertices = affected;
    meshes.push({ mesh, source });
    geometries.add(geometry);
  });
  return {
    meshes: meshes.map(({ mesh }) => mesh),
    update(amount) {
      if (disposed) return;
      const value = Number.isFinite(amount) ? Math.max(0, Math.min(1, amount)) : 0;
      for (const { mesh } of meshes) mesh.morphTargetInfluences[0] = value;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      for (const { mesh, source } of meshes) {
        mesh.geometry = source;
        mesh.updateMorphTargets();
      }
      for (const geometry of geometries) geometry.dispose();
      geometries.clear();
    },
  };
}

/** Twelve soft vapour cards remain anchored to the measured animated nostrils.
 * The inhale draws inward; exhale drifts outward. They carry no light or damage. */
export function createSkyNostrilVapour(T, scene, own) {
  const geometry = own(new T.PlaneGeometry(2, 2));
  const material = own(
    new T.ShaderMaterial({
      uniforms: { time: { value: 0 }, strength: { value: 0 } },
      transparent: true,
      depthWrite: false,
      side: T.DoubleSide,
      toneMapped: false,
      vertexShader: `varying vec2 mistUv;
      void main(){mistUv=uv;gl_Position=projectionMatrix*modelViewMatrix*instanceMatrix*vec4(position,1.0);}`,
      fragmentShader: `uniform float time,strength;varying vec2 mistUv;
      void main(){vec2 p=mistUv*2.0-1.0;
        float ripple=sin(p.x*11.0+time*3.0)*sin(p.y*9.0-time*2.0);
        float edge=1.0-smoothstep(.15,1.0,length(p)+ripple*.12);
        gl_FragColor=vec4(vec3(.48,.38,.28),edge*strength*.105);}`,
    }),
  );
  const vapour = new T.InstancedMesh(geometry, material, 12);
  vapour.name = 'Measured nostril warm vapour';
  vapour.frustumCulled = false;
  vapour.count = 0;
  scene.add(vapour);
  const pose = new T.Object3D(),
    orientation = new T.Quaternion(),
    direction = new T.Vector3();
  let disposed = false;
  return {
    vapour,
    draw(state, camera, quiet) {
      const anatomy = state.boss?.anatomy,
        breathing = anatomy?.breathing;
      const anchors = anatomy?.nostrils;
      const strength = breathing?.nostrilFlare ?? 0;
      vapour.count =
        !quiet &&
        !state.traversal &&
        !state.boss?.defeated &&
        anchors?.length === 2 &&
        strength > 0.08
          ? 12
          : 0;
      if (!vapour.count) return;
      material.uniforms.time.value = state.time;
      material.uniforms.strength.value = strength;
      if (camera) camera.getWorldQuaternion(orientation);
      else orientation.identity();
      for (let index = 0; index < vapour.count; index++) {
        const anchor = anchors[index % 2],
          phase = (((state.time * 1.8 + Math.floor(index / 2) / 6) % 1) + 1) % 1;
        const outward = breathing.phase === 'exhale' ? phase : 1 - phase;
        const scale = SKY_WORLD_ASSETS.dragon.modelScale;
        direction.set(anchor.dx, anchor.dy, anchor.dz).normalize();
        pose.position
          .set(anchor.x, anchor.y, anchor.z)
          .addScaledVector(direction, (outward * 0.22 + strength * 0.014) * scale);
        pose.position.y += outward * outward * 0.06 * scale;
        const size = (0.012 + outward * 0.047) * scale * strength;
        pose.scale.set(size, size * 1.65, 1);
        pose.quaternion.copy(orientation);
        pose.rotateZ(index * 2.399);
        pose.updateMatrix();
        vapour.setMatrixAt(index, pose.matrix);
      }
      vapour.instanceMatrix.needsUpdate = true;
    },
    warmup: () => new T.InstancedMesh(geometry, material, 1),
    dispose() {
      if (disposed) return;
      disposed = true;
      vapour.removeFromParent();
      vapour.dispose();
    },
  };
}
